import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Button } from '@/shared/ui/Button'
import { Modal } from '@/shared/ui/Modal'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Spinner } from '@/shared/ui/Spinner'
import { Pagination } from '@/shared/ui/Pagination'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { cn, pickTranslation } from '@/shared/utils'
import { getPaymentMethodLabel } from '@/shared/constants'
import { useDebounce } from '@/shared/hooks'
import {
  useAllInOneEnrollments, useAllInOnePaidEnrollments, useAllInOneEnrollment, useCities,
  useAssignEnrollmentTrainer, useCancelEnrollment, useMarkEnrollmentPaid, useUpdateEnrollmentItemStatus,
} from '@/api/hooks'
import type { AllInOneEnrollment, AllInOneEnrollmentItem } from '@/api/services/allInOneService'
import {
  Users, CheckCircle2, Clock, Calendar, Wallet, UserCog, Ban, ChevronRight,
  Phone, MapPin, Edit, AlertTriangle, Search, ChevronDown, X, CreditCard,
} from 'lucide-react'

function Field({ label, className, ...props }: { label?: string; className?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>}
      <input {...props}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"/>
    </div>
  )
}
function TextareaField({ label, className, ...props }: { label?: string; className?: string } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>}
      <textarea {...props} rows={props.rows ?? 2}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"/>
    </div>
  )
}
function SelectField({ label, className, children, ...props }: { label?: string; className?: string; children: React.ReactNode } & React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>}
      <select {...props}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
        {children}
      </select>
    </div>
  )
}

// A searchable single-select — a plain <select> with 500 trainers in it is
// unusable, this lets you type a name to narrow it down instead of scrolling.
// The search term itself is sent to the server (see TrainerAndPackageFields)
// so it matches across every trainer, not just whatever page has loaded so far.
function TrainerPicker({ label, trainers, loading, value, onChange, search, onSearchChange, hasMore, loadingMore, onLoadMore }: {
  label: string; trainers: any[]; loading?: boolean; value: string; onChange: (id: string) => void
  search: string; onSearchChange: (v: string) => void
  hasMore?: boolean; loadingMore?: boolean; onLoadMore?: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selected = trainers.find(tr => String(tr.id) === value)

  return (
    <div className="relative" ref={ref}>
      <label className="text-xs font-semibold text-gray-500 block mb-1">{label}</label>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
        <span className={cn('truncate', selected ? 'text-gray-800' : 'text-gray-400')}>
          {selected ? (selected.name ?? `#${selected.id}`) : (loading ? t('allInOneEnrollments.loadingTrainers') : t('allInOneEnrollments.selectTrainer'))}
        </span>
        <ChevronDown size={14} className={cn('text-gray-400 flex-shrink-0 transition-transform', open && 'rotate-180')}/>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={12} className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input autoFocus value={search} onChange={e => onSearchChange(e.target.value)}
                placeholder={t('allInOneEnrollments.searchTrainers')}
                className="w-full ps-7 pe-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400"/>
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto"
            onScroll={e => {
              if (!hasMore || loadingMore || !onLoadMore) return
              const el = e.currentTarget
              if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) onLoadMore()
            }}>
            {loading ? (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">{t('allInOneEnrollments.loadingTrainers')}</div>
            ) : trainers.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">{t('allInOneEnrollments.noTrainersFound')}</div>
            ) : trainers.map(tr => (
              <button key={tr.id} type="button"
                onClick={() => { onChange(String(tr.id)); setOpen(false); onSearchChange('') }}
                className={cn('w-full text-left px-3 py-2 text-xs hover:bg-brand-50 transition-colors',
                  String(tr.id) === value ? 'bg-brand-50 font-semibold text-brand-700' : 'text-gray-700')}>
                {tr.name ?? `#${tr.id}`}
              </button>
            ))}
            {hasMore && (
              <button type="button" onClick={() => onLoadMore?.()} disabled={loadingMore}
                className="w-full text-center px-3 py-2 text-xs font-semibold text-brand-600 hover:bg-brand-50 disabled:opacity-50">
                {loadingMore ? t('allInOneEnrollments.loadingTrainers') : t('allInOneEnrollments.loadMoreTrainers')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// The list endpoint paginates and caps out at ~10 rows per page regardless of
// a bigger `limit`, so loading "all trainers" up front in one shot isn't an
// option — this fetches one page at a time, appended on demand as the admin
// scrolls/asks for more in the picker below. `search` is sent to the server
// (not filtered client-side) so it matches across every trainer, not just
// whichever page has loaded so far — pagination within those results still
// stays at 10 per page.
const TRAINERS_PAGE_SIZE = 10
async function fetchTrainersPage(page: number, search: string): Promise<{ rows: any[]; total: number }> {
  const params = new URLSearchParams({ page: String(page), limit: String(TRAINERS_PAGE_SIZE) })
  params.append('user_types[]', 'TRAINER')
  if (search.trim()) params.append('search', search.trim())
  const res: any = await restClient.get(`${endpoints.users.list}?${params.toString()}`).then(r => r.data)
  const rows: any[] = res?.data ?? []
  const total = res?.total ?? rows.length
  return { rows, total }
}

// Shared by AssignTrainerModal and the practical-lessons section of
// ItemStatusModal — trainer picker + a package list scoped to that trainer.
// The trainer's own packages come back directly on their user-detail record
// (admin/user/get-user/:id → data.packages) — same source UserDetailsPage
// reads for the trainer's own "Packages" tab — not the vendor-catalog lookup,
// which is a different list (a school's whole catalog, not what's actually
// assigned to this one trainer).
function TrainerAndPackageFields({ trainerId, onTrainerChange, packageId, onPackageChange, enabled = true }: {
  trainerId: string; onTrainerChange: (id: string) => void
  packageId: string; onPackageChange: (id: string) => void
  enabled?: boolean
}) {
  const { t } = useTranslation()
  const [trainerSearch, setTrainerSearch] = useState('')
  const debouncedTrainerSearch = useDebounce(trainerSearch, 400)

  const {
    data: trainerPages, isLoading: loadingTrainers,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['users-for-all-in-one-trainer-assign', debouncedTrainerSearch],
    queryFn: ({ pageParam }) => fetchTrainersPage(pageParam, debouncedTrainerSearch),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.rows.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
    enabled,
    staleTime: 60_000,
  })
  const trainers = (trainerPages?.pages ?? []).flatMap(p => p.rows)

  const { data: trainerDetail, isFetching: loadingPackages } = useQuery({
    queryKey: ['all-in-one-trainer-detail', trainerId],
    queryFn: () => restClient.get(`${endpoints.users.getbyId}${trainerId}`).then(r => r.data),
    enabled: enabled && !!trainerId,
    staleTime: 30_000,
  })
  // Each row here is a trainer-package mapping, not the package itself — the
  // actual package (id, name via per-locale translations, price) is nested
  // under `.package`. The mapping's own `id` is NOT the package id; the real
  // one is `package_id` / `package.id`.
  const packages = ((trainerDetail as any)?.data?.packages ?? [])
    .filter((p: any) => p.is_published !== 0 && p.is_published !== false)
    .map((p: any) => {
      const pkg = p.package ?? {}
      const tr = pkg.package_translations?.find((t: any) => t?.locale?.toLowerCase() === 'en') ?? pkg.package_translations?.[0]
      const id = p.package_id ?? pkg.id ?? p.id
      return { id, name: tr?.name ?? pkg.name ?? `#${id}`, price: p.price ?? pkg.price }
    })

  return (
    <>
      <TrainerPicker label={t('allInOneEnrollments.trainer')} trainers={trainers} loading={loadingTrainers}
        value={trainerId} onChange={id => { onTrainerChange(id); onPackageChange('') }}
        search={trainerSearch} onSearchChange={setTrainerSearch}
        hasMore={!!hasNextPage} loadingMore={isFetchingNextPage} onLoadMore={fetchNextPage}/>
      <SelectField label={t('allInOneEnrollments.package')} value={packageId} onChange={e => onPackageChange(e.target.value)}
        disabled={!trainerId || loadingPackages}>
        <option value="">
          {!trainerId ? t('allInOneEnrollments.selectTrainerFirst')
            : loadingPackages ? t('allInOneEnrollments.loadingPackages')
            : packages.length === 0 ? t('allInOneEnrollments.noPackagesForTrainer')
            : t('allInOneEnrollments.selectPackage')}
        </option>
        {packages.map((p: any) => (
          <option key={p.id} value={p.id}>{p.name}{p.price != null ? ` — ${p.price} AED` : ''}</option>
        ))}
      </SelectField>
    </>
  )
}

function fmtDT(v?: string | null) {
  if (!v) return '—'
  try {
    const d = new Date(v)
    if (isNaN(d.getTime())) return v
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · '
      + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  } catch { return v }
}


// The enrollment's own numeric `status`/`payment_status` codes have no
// published enum, so labels are derived from the one thing that's honest and
// unambiguous either way: whether the relevant timestamp/amount is actually
// set, rather than guessing what each code number means.
function paymentBadge(e: AllInOneEnrollment, t: any) {
  if (e.paid_at || (e.amount_paid != null && e.amount != null && Number(e.amount_paid) >= Number(e.amount))) {
    return { label: t('allInOneEnrollments.paid'), cls: 'bg-emerald-50 text-emerald-600' }
  }
  if (e.amount_paid) return { label: t('allInOneEnrollments.partiallyPaid'), cls: 'bg-amber-50 text-amber-600' }
  return { label: t('allInOneEnrollments.unpaid'), cls: 'bg-gray-100 text-gray-500' }
}
// Confirmed status codes: 1 = scheduled, 2 = in progress, 3 = complete
// (anything else, i.e. 0/unset, means it hasn't been touched yet).
const ITEM_STATUS: { value: number; key: 'scheduled' | 'inProgress' | 'done' }[] = [
  { value: 1, key: 'scheduled' }, { value: 2, key: 'inProgress' }, { value: 3, key: 'done' },
]
function itemStateOf(item: AllInOneEnrollmentItem) {
  return ITEM_STATUS.find(s => s.value === item.status)?.key ?? 'pending'
}
function itemStateCls(state: string) {
  if (state === 'done') return 'bg-emerald-50 text-emerald-600'
  if (state === 'inProgress') return 'bg-sky-50 text-sky-600'
  if (state === 'scheduled') return 'bg-amber-50 text-amber-600'
  return 'bg-gray-100 text-gray-500'
}

// RTA exam appointments — these are the only items that get a schedule date,
// test location and pass/fail result; "File opening" and "Practical lessons"
// (which has its own trainer/package flow above) don't need them.
const EXAM_ITEM_CODES = ['theory_class', 'parking_test', 'road_test']
function isExamItem(item: AllInOneEnrollmentItem | null) {
  return !!item && EXAM_ITEM_CODES.includes(item.code ?? '')
}

// datetime-local <input> needs "YYYY-MM-DDTHH:mm" in local time, the API gives/wants an ISO string.
function toDatetimeLocal(iso?: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
function fromDatetimeLocal(local: string) {
  if (!local) return null
  const d = new Date(local)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

// ══════════════════════════════════════════════════════════════════════════
// Update one item's progress — POST enrollment-item/:id/status. For the
// practical-lessons item specifically, this is also where trainer + package
// get assigned (POST enrollment/:id/assign-trainer) — the two are separate
// endpoints under the hood, but one place to do both since that's where an
// admin naturally goes looking for it.
// ══════════════════════════════════════════════════════════════════════════
function ItemStatusModal({ open, onClose, item, enrollmentId, currentTrainerId }: {
  open: boolean; onClose: () => void; item: AllInOneEnrollmentItem | null
  enrollmentId: number | null; currentTrainerId?: number | null
}) {
  const { t } = useTranslation()
  const update = useUpdateEnrollmentItemStatus()
  const assign = useAssignEnrollmentTrainer()
  const [status, setStatus] = useState('')
  const [note, setNote] = useState('')
  const [trainerId, setTrainerId] = useState('')
  const [packageId, setPackageId] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [location, setLocation] = useState('')
  const [result, setResult] = useState('')

  const isPractical = item?.code === 'practical'
  const isExam = isExamItem(item)

  const [seededFor, setSeededFor] = useState<number | null>(null)
  if (open && item && item.id !== seededFor) {
    setSeededFor(item.id)
    setStatus(item.status != null ? String(item.status) : '')
    setNote(item.note ?? '')
    setTrainerId(currentTrainerId ? String(currentTrainerId) : '')
    setPackageId('')
    setScheduledAt(toDatetimeLocal(item.scheduled_at))
    setLocation(item.location ?? '')
    setResult(item.result ?? '')
  }

  const submitStatus = async () => {
    if (!item) return
    try {
      await update.mutateAsync({
        id: item.id, status: status === '' ? 0 : Number(status),
        note: note || undefined,
        ...(isExam ? {
          scheduled_at: fromDatetimeLocal(scheduledAt),
          location: location || undefined,
          result: result || undefined,
        } : {}),
        payload: {},
      })
      toast.success(t('allInOneEnrollments.itemUpdated'))
      onClose()
    } catch (e) { toast.error(getApiError(e)) }
  }

  const submitAssignTrainer = async () => {
    if (!enrollmentId) return
    if (!trainerId) { toast.error(t('allInOneEnrollments.errTrainerRequired')); return }
    if (!packageId) { toast.error(t('allInOneEnrollments.errPackageRequired')); return }
    try {
      await assign.mutateAsync({ id: enrollmentId, trainer_id: Number(trainerId), package_id: Number(packageId), note: note || undefined })
      toast.success(t('allInOneEnrollments.trainerAssigned'))
      onClose()
    } catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <Modal open={open} onClose={onClose} title={item ? t('allInOneEnrollments.updateItemTitle', { title: item.title }) : ''} size="sm">
      {item && (
        <div className="space-y-3">
          <SelectField label={t('allInOneEnrollments.statusCode')} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">{t('allInOneEnrollments.itemState.pending')}</option>
            {ITEM_STATUS.map(s => <option key={s.value} value={s.value}>{t(`allInOneEnrollments.itemState.${s.key}`)}</option>)}
          </SelectField>
          <TextareaField label={t('allInOneEnrollments.note')} value={note} onChange={e => setNote(e.target.value)}/>
          {isExam && (
            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 space-y-3">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('allInOneEnrollments.examDetails')}</span>
              <Field label={t('allInOneEnrollments.scheduledAt')} type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}/>
              <Field label={t('allInOneEnrollments.location')} placeholder="e.g. RTA Al Barsha" value={location} onChange={e => setLocation(e.target.value)}/>
              <SelectField label={t('allInOneEnrollments.result')} value={result} onChange={e => setResult(e.target.value)}>
                <option value="">{t('allInOneEnrollments.resultNotSet')}</option>
                <option value="pass">{t('allInOneEnrollments.resultPass')}</option>
                <option value="fail">{t('allInOneEnrollments.resultFail')}</option>
              </SelectField>
            </div>
          )}
          <Button className="w-full justify-center" loading={update.isPending} onClick={submitStatus}>{t('common.saveChanges')}</Button>
          {isPractical && (
            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 space-y-3">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('allInOneEnrollments.assignTrainer')}</span>
              <TrainerAndPackageFields trainerId={trainerId} onTrainerChange={setTrainerId} packageId={packageId} onPackageChange={setPackageId} enabled={open && isPractical}/>
              <Button className="w-full justify-center" variant="secondary" loading={assign.isPending} onClick={submitAssignTrainer}>{t('allInOneEnrollments.assignTrainer')}</Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Mark paid — POST enrollment/:id/mark-paid (offline payments)
// ══════════════════════════════════════════════════════════════════════════
function MarkPaidModal({ open, onClose, enrollment }: { open: boolean; onClose: () => void; enrollment: AllInOneEnrollment | null }) {
  const { t } = useTranslation()
  const markPaid = useMarkEnrollmentPaid()
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')

  const [seededFor, setSeededFor] = useState<number | null>(null)
  if (open && enrollment && enrollment.id !== seededFor) {
    setSeededFor(enrollment.id)
    setAmount(enrollment.amount != null ? String(enrollment.amount) : '')
    setReference(''); setNote('')
  }

  const submit = async () => {
    if (!enrollment) return
    try {
      await markPaid.mutateAsync({
        id: enrollment.id,
        amount_paid: amount ? Number(amount) : undefined,
        reference: reference || undefined, note: note || undefined,
      })
      toast.success(t('allInOneEnrollments.markedPaid'))
      onClose()
    } catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <Modal open={open} onClose={onClose} title={t('allInOneEnrollments.markPaid')} size="sm">
      {enrollment && (
        <div className="space-y-3">
          <Field label={t('allInOneEnrollments.amountPaid')} type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)}/>
          <Field label={t('allInOneEnrollments.reference')} placeholder="e.g. bank transfer ref" value={reference} onChange={e => setReference(e.target.value)}/>
          <TextareaField label={t('allInOneEnrollments.note')} value={note} onChange={e => setNote(e.target.value)}/>
          <Button className="w-full justify-center" loading={markPaid.isPending} onClick={submit}>{t('allInOneEnrollments.markPaid')}</Button>
        </div>
      )}
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Enrollment detail drawer
// ══════════════════════════════════════════════════════════════════════════
function EnrollmentDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data, isLoading } = useAllInOneEnrollment(id)
  const enrollment = data?.data
  const cancel = useCancelEnrollment()

  const [markPaidOpen, setMarkPaidOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [itemModalItem, setItemModalItem] = useState<AllInOneEnrollmentItem | null>(null)

  if (isLoading || !enrollment) {
    return (
      <Modal open onClose={onClose} title={t('allInOneEnrollments.detailTitle')} size="lg">
        <div className="flex justify-center py-14"><Spinner/></div>
      </Modal>
    )
  }

  const snap = enrollment.snapshot ?? enrollment.variant
  const pay = paymentBadge(enrollment, t)
  const items = enrollment.items ?? []
  const isCancelled = !!enrollment.cancelled_at
  const isFullyPaid = pay.label === t('allInOneEnrollments.paid')

  return (
    <>
      <Modal open onClose={onClose} title={t('allInOneEnrollments.detailTitle')} size="lg">
        <div className="space-y-5">
          {/* Summary */}
          <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-lg font-black text-gray-900">{enrollment.student_name ?? `#${enrollment.user_id}`}</div>
                <div className="text-xs text-gray-500">{snap?.title}</div>
                {enrollment.student_phone && (
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5"><Phone size={11}/> {enrollment.student_phone}</div>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200 text-[11px] font-bold text-gray-600">{enrollment.status_label ?? enrollment.status}</span>
                <span className={cn('px-2.5 py-1 rounded-full text-[11px] font-bold', pay.cls)}>{pay.label}</span>
                {isCancelled && <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[11px] font-bold">{t('allInOneEnrollments.cancelled')}</span>}
              </div>
            </div>
            {enrollment.progress && (
              <div className="mb-3">
                <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
                  <span>{t('allInOneEnrollments.progress')}</span>
                  <span className="font-bold">{enrollment.progress.done}/{enrollment.progress.total} · {enrollment.progress.percent}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${enrollment.progress.percent}%`, background: '#f68b2c' }}/>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div className="flex items-center gap-1.5 text-gray-500"><Wallet size={12}/> {t('allInOneEnrollments.amountPaid')}: {enrollment.amount_paid ?? 0} {enrollment.currency}</div>
              <div className="flex items-center gap-1.5 text-gray-500"><Calendar size={12}/> {fmtDT(enrollment.created_at)}</div>
              <div className="flex items-center gap-1.5 text-gray-500"><UserCog size={12}/> {enrollment.trainer?.name ?? (enrollment.trainer_id ? `#${enrollment.trainer_id}` : t('allInOneEnrollments.noTrainer'))}</div>
              {enrollment.payment_method != null && (
                <div className="flex items-center gap-1.5 text-gray-500"><CreditCard size={12}/> {getPaymentMethodLabel(enrollment.payment_method)}</div>
              )}
            </div>
          </div>

          {/* Actions */}
          {!isCancelled && (
            <div className="flex flex-wrap items-center gap-2">
              {!isFullyPaid && (
                <Button size="sm" variant="secondary" icon={<Wallet size={13}/>} onClick={() => setMarkPaidOpen(true)}>{t('allInOneEnrollments.markPaid')}</Button>
              )}
              <Button size="sm" variant="danger" icon={<Ban size={13}/>} loading={cancelling} onClick={async () => {
                setCancelling(true)
                try { await cancel.mutateAsync({ id: enrollment.id }); toast.success(t('allInOneEnrollments.enrollmentCancelled')) }
                catch (e) { toast.error(getApiError(e)) }
                finally { setCancelling(false) }
              }}>{t('allInOneEnrollments.cancelEnrollment')}</Button>
            </div>
          )}

          {/* Items checklist */}
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{t('allInOneEnrollments.itemsHeading')}</h4>
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {items.map((item, i) => {
                const state = itemStateOf(item)
                return (
                  <div key={item.id} className="flex items-center gap-3 px-3.5 py-2.5 bg-white hover:bg-gray-50 transition-colors">
                    <span className="w-6 h-6 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-400 flex-shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-800 truncate">{item.title}</div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-400">
                        {item.sessions_total != null && <span>{item.sessions_done ?? 0}/{item.sessions_total} {t('allInOnePackage.sessionsCount').toLowerCase()}</span>}
                        {item.location && <span className="flex items-center gap-0.5"><MapPin size={10}/>{item.location}</span>}
                        {item.result && <span className="italic">"{item.result}"</span>}
                      </div>
                    </div>
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0', itemStateCls(state))}>
                      {state === 'done' && <CheckCircle2 size={10} className="inline me-1 -mt-0.5"/>}
                      {state === 'scheduled' && <Clock size={10} className="inline me-1 -mt-0.5"/>}
                      {t(`allInOneEnrollments.itemState.${state}`)}
                    </span>
                    <button onClick={() => setItemModalItem(item)}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50"><Edit size={13}/></button>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </Modal>

      <MarkPaidModal open={markPaidOpen} onClose={() => { setMarkPaidOpen(false); qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollment', id] }) }} enrollment={enrollment}/>
      <ItemStatusModal open={!!itemModalItem} onClose={() => { setItemModalItem(null); qc.invalidateQueries({ queryKey: ['all-in-one', 'enrollment', id] }) }}
        item={itemModalItem} enrollmentId={enrollment.id} currentTrainerId={enrollment.trainer_id}/>
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Enrollment card — a solid-color top bar signals status at a glance; name,
// package, a progress bar, the paid/total amount and date, then status pills.
// ══════════════════════════════════════════════════════════════════════════
// Falls back to initials if there's no photo, or the photo URL 404s/fails to load.
function StudentAvatar({ name, photoUrl, size = 36 }: { name?: string; photoUrl?: string | null; size?: number }) {
  const [failed, setFailed] = useState(false)
  if (photoUrl && !failed) {
    return <img src={photoUrl} alt="" onError={() => setFailed(true)}
      style={{ width: size, height: size }} className="rounded-full object-cover flex-shrink-0 bg-gray-100"/>
  }
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-brand-50 text-brand-600 flex items-center justify-center font-bold text-sm flex-shrink-0">
      {(name ?? '?').slice(0, 1).toUpperCase()}
    </div>
  )
}

function EnrollmentCard({ enrollment, cityName, onClick }: { enrollment: AllInOneEnrollment; cityName: string | null; onClick: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const snap = enrollment.snapshot ?? enrollment.variant
  const pay = paymentBadge(enrollment, t)
  const isCancelled = !!enrollment.cancelled_at
  const topBar = isCancelled ? 'bg-red-400' : pay.label === t('allInOneEnrollments.paid') ? 'bg-emerald-400' : 'bg-amber-400'

  return (
    <div onClick={onClick} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') onClick() }}
      className="group text-start bg-white rounded-2xl border border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:shadow-lg hover:border-gray-300 transition-all overflow-hidden flex flex-col cursor-pointer">
      <div className={cn('h-1.5 w-full', topBar)}/>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/user/${enrollment.user_id}`) }}
            className="flex items-center gap-2.5 min-w-0 text-start hover:opacity-80 transition-opacity">
            <StudentAvatar name={enrollment.student_name} photoUrl={enrollment.student_photo_url} size={40}/>
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 truncate group-hover:text-brand-600">{enrollment.student_name ?? `#${enrollment.user_id}`}</div>
              <div className="text-[11px] text-gray-400 truncate">{snap?.title}</div>
              {enrollment.student_phone && (
                <div className="flex items-center gap-1 text-[11px] text-gray-400 truncate">
                  <Phone size={10} className="flex-shrink-0"/>{enrollment.student_phone}
                </div>
              )}
              {cityName && (
                <div className="flex items-center gap-1 text-[11px] text-gray-400 truncate">
                  <MapPin size={10} className="flex-shrink-0"/>{cityName}
                </div>
              )}
            </div>
          </button>
          <ChevronRight size={15} className="text-gray-300 flex-shrink-0 mt-1.5 group-hover:text-brand-400 transition-colors"/>
        </div>

        {enrollment.progress && (
          <div>
            <div className="flex items-center justify-between text-[10px] text-gray-400 mb-1">
              <span>{t('allInOneEnrollments.progress')}</span>
              <span className="font-bold">{enrollment.progress.done}/{enrollment.progress.total} &middot; {enrollment.progress.percent}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${enrollment.progress.percent}%`, background: '#f68b2c' }}/>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-50">
          <span className="flex items-center gap-1"><Wallet size={11} className="text-gray-300"/>{enrollment.amount_paid ?? 0}/{enrollment.amount ?? 0} {enrollment.currency}</span>
          <span className="flex items-center gap-1 whitespace-nowrap"><Calendar size={11} className="text-gray-300"/>{fmtDT(enrollment.created_at)}</span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-auto">
          <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold', pay.cls)}>{pay.label}</span>
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold">{enrollment.status_label ?? enrollment.status}</span>
          {enrollment.payment_method != null && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold">
              <CreditCard size={10}/>{getPaymentMethodLabel(enrollment.payment_method)}
            </span>
          )}
          {isCancelled && <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[10px] font-bold">{t('allInOneEnrollments.cancelled')}</span>}
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════════════
export function AllInOneEnrollmentsPage() {
  const { t, i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  // One server-side status per chip (the API takes a single `status`), so
  // paging and totals always match what's filtered.
  const [status, setStatus] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const userIdFilter = searchParams.get('user_id') ? Number(searchParams.get('user_id')) : undefined
  const isPaidView = status === 'paid'
  const pagedQ = useAllInOneEnrollments({ page, limit: perPage, user_id: userIdFilter, status: isPaidView ? undefined : status || undefined }, !isPaidView)
  const paidQ = useAllInOnePaidEnrollments({ user_id: userIdFilter }, isPaidView)
  const { isLoading, isFetching, isError } = isPaidView ? paidQ : pagedQ
  const enrollments: AllInOneEnrollment[] = isPaidView
    ? (paidQ.data ?? []).slice((page - 1) * perPage, page * perPage)
    : pagedQ.data?.data ?? []
  const total = isPaidView ? paidQ.data?.length ?? 0 : pagedQ.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / perPage))

  const changeStatus = (s: string) => { setStatus(s); setPage(1) }
  const goToPage = (p: number) => { setPage(Math.min(Math.max(1, p), lastPage)); window.scrollTo({ top: 0, behavior: 'smooth' }) }
  const STATUS_GROUPS: { label: string; chips: { value: string; label: string; dot: string }[] }[] = [
    { label: t('allInOneEnrollments.filter.unpaid'), chips: [{ value: '0', label: t('allInOneEnrollments.filter.pendingPayment'), dot: 'bg-amber-400' }] },
    { label: t('allInOneEnrollments.filter.paid'), chips: [
      { value: 'paid', label: t('allInOneEnrollments.filter.allPaid'), dot: 'bg-emerald-500' },
      { value: '1', label: t('allInOneEnrollments.filter.active'), dot: 'bg-emerald-400' },
      { value: '2', label: t('allInOneEnrollments.filter.completed'), dot: 'bg-emerald-600' },
    ] },
    { label: t('allInOneEnrollments.filter.other'), chips: [
      { value: '3', label: t('allInOneEnrollments.filter.cancelled'), dot: 'bg-red-400' },
      { value: '4', label: t('allInOneEnrollments.filter.refunded'), dot: 'bg-gray-400' },
    ] },
  ]
  const studentNameFilter = enrollments[0]?.student_name

  const { data: citiesData } = useCities({ limit: 500 })
  const cities: any[] = citiesData?.data ?? []
  const cityNameOf = (cityId?: number | null) => {
    if (!cityId) return null
    const city = cities.find((c: any) => c.id === cityId)
    return pickTranslation(city?.city_translations, i18n.language)?.name ?? city?.name ?? null
  }

  const [openId, setOpenId] = useState<number | null>(null)

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('allInOneEnrollments.title')}
        subtitle={t('allInOneEnrollments.subtitle')}
        breadcrumbs={[{ label: t('allInOnePackage.breadcrumbDashboard'), to: '/dashboard' }, { label: t('allInOnePackage.title'), to: '/dashboard/all-in-one' }, { label: t('allInOneEnrollments.title') }]}
      />

      {userIdFilter && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-brand-50 border border-brand-100 text-xs text-brand-700 w-fit">
          <span className="font-semibold">{t('allInOneEnrollments.filteredByStudent', { name: studentNameFilter ?? `#${userIdFilter}` })}</span>
          <button onClick={() => setSearchParams({})} className="text-brand-500 hover:text-brand-700"><X size={13}/></button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        <button onClick={() => changeStatus('')}
          className={cn('px-3 py-1.5 rounded-full text-xs font-bold border transition-colors',
            status === '' ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')}>
          {t('allInOneEnrollments.filter.all')}
        </button>
        {STATUS_GROUPS.map(g => (
          <div key={g.label} className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide me-1">{g.label}</span>
            {g.chips.map(c => (
              <button key={c.value} onClick={() => changeStatus(c.value)}
                className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors',
                  status === c.value ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')}>
                <span className={cn('w-1.5 h-1.5 rounded-full', status === c.value ? 'bg-white' : c.dot)}/>{c.label}
              </button>
            ))}
          </div>
        ))}
        <span className={cn('ms-auto text-xs text-gray-400', isFetching && 'animate-pulse')}>
          {t('allInOneEnrollments.filter.resultCount', { count: total })}
        </span>
      </div>

      {isError && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          <AlertTriangle size={16} className="flex-shrink-0"/> {t('allInOneEnrollments.loadError')}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner/></div>
      ) : enrollments.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <EmptyState icon={<Users size={22}/>} message={status ? t('allInOneEnrollments.filter.noneForFilter') : t('allInOneEnrollments.noEnrollments')}/>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {enrollments.map(e => (
            <EnrollmentCard key={e.id} enrollment={e} cityName={cityNameOf(e.city_id)} onClick={() => setOpenId(e.id)}/>
          ))}
        </div>
      )}

      {total > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Pagination page={page} lastPage={lastPage} total={total} perPage={perPage} onPageChange={goToPage}
            pageSizeOptions={[10, 20, 50]} onPerPageChange={n => { setPerPage(n); setPage(1) }}/>
        </div>
      )}

      {openId != null && <EnrollmentDetail id={openId} onClose={() => setOpenId(null)}/>}
    </div>
  )
}
